import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { injectInterFont } from "../utils/fonts";

export type OverflowAction = "new" | "recent" | "end" | "details";

@customElement("cg-overflow-menu")
export class CGOverflowMenu extends LitElement {
  static override styles = css`
    :host {
      position: relative;
      display: inline-block;
      font-family: "Inter", sans-serif !important;
    }

    *,
    *::before,
    *::after {
      font-family: "Inter", sans-serif !important;
    }

    button {
      font: inherit;
      font-family: "Inter", sans-serif !important;
      font-synthesis: none;
      letter-spacing: 0;
    }

    :host([disabled]) {
      opacity: 0.45;
      pointer-events: none;
    }

    :host([disabled]) button.trigger {
      cursor: not-allowed;
    }

    /* ===========================
     Trigger Button
  ============================ */

    button.trigger {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      border: 0;
      background: transparent;
      cursor: pointer;
      display: grid;
      place-items: center;
      font-size: 18px;
      color: inherit; /* ✅ Inherit from header */

      transition:
        background-color 120ms ease,
        transform 120ms ease,
        color 120ms ease;
    }

    button.trigger:hover {
      background: rgba(0, 0, 0, 0.06);
      color: var(--cg-text);
    }

    button.trigger:active {
      transform: scale(0.92);
    }

    /* ===========================
     Menu Surface
  ============================ */

    .menu {
      position: absolute;
      top: 40px;
      right: 0;
      min-width: 200px;
      padding: 6px;
      background: color-mix(in srgb, var(--cg-bg) 92%, transparent);
      border: 1px solid var(--cg-border);
      border-radius: 14px;
      backdrop-filter: blur(10px);
      box-shadow:
        0 10px 30px rgba(0, 0, 0, 0.12),
        0 2px 10px rgba(0, 0, 0, 0.06);
      z-index: 60;

      animation: menuIn 140ms ease-out;
      transform-origin: top right;
    }

    @keyframes menuIn {
      from {
        opacity: 0;
        transform: translateY(-4px) scale(0.96);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    /* ===========================
     Menu Items
  ============================ */

    .item {
      font-family: "Inter", sans-serif !important;
      width: 100%;
      padding: 10px 12px;
      border-radius: 10px;
      background: transparent;
      border: none;
      text-align: left;
      cursor: pointer;

      display: flex;
      align-items: center;
      gap: 10px;

      font-size: 13px;
      font-weight: 500;
      color: var(--cg-text);
      font-synthesis: none;
      letter-spacing: 0;

      transition:
        background-color 120ms ease,
        transform 120ms ease,
        color 120ms ease;
    }

    .item:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    .item:active {
      transform: scale(0.98);
    }

    /* ===========================
     Destructive Action
  ============================ */

    .item.danger {
      color: #d92d20;
    }

    .item.danger:hover {
      background: color-mix(in srgb, #d92d20 12%, transparent);
    }

    /* ===========================
     Separator
  ============================ */

    .sep {
      height: 1px;
      background: var(--cg-border);
      margin: 6px 4px;
    }
  `;

  @property({ type: Boolean }) open = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  /** Emits selected action */
  @property({ attribute: false }) onAction?: (action: OverflowAction) => void;

  override connectedCallback(): void {
    super.connectedCallback();
    injectInterFont(this.shadowRoot ?? this);
    document.addEventListener("click", this.handleOutside);
    document.addEventListener("keydown", this.handleEscape);
  }

  override disconnectedCallback(): void {
    document.removeEventListener("click", this.handleOutside);
    document.removeEventListener("keydown", this.handleEscape);
    super.disconnectedCallback();
  }

  private handleOutside = (e: MouseEvent) => {
    if (!this.open) return;
    if (!this.contains(e.target as Node)) {
      this.open = false;
    }
  };

  private handleEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.open = false;
    }
  };

  private toggle(): void {
    if (this.disabled) return; // 🛑 block interaction
    this.open = !this.open;
  }

  private fire(action: OverflowAction): void {
    if (this.disabled) return; // 🛑 block action
    this.open = false;
    this.onAction?.(action);
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has("disabled") && this.disabled) {
      this.open = false;
    }
  }

  override render() {
    return html`
      <button
        ?disabled=${this.disabled}
        class="trigger"
        aria-haspopup="menu"
        aria-expanded=${this.open}
        aria-label="Menu"
        @click=${(e: MouseEvent) => {
          e.stopPropagation();
          this.toggle();
        }}
      >
        ⋮
      </button>

      ${this.open
        ? html`
            <div class="menu" role="menu">
              <button
                class="item"
                role="menuitem"
                @click=${() => this.fire("new")}
              >
                Start new chat
              </button>

              <button
                class="item"
                role="menuitem"
                @click=${() => this.fire("recent")}
              >
                View recent chats
              </button>

              <!-- <button
                class="item"
                role="menuitem"
                @click=${() => this.fire("details")}
              >
                Add details
              </button>

              <div class="sep"></div> -->

              <button
                class="item"
                role="menuitem"
                @click=${() => this.fire("end")}
              >
                End current chat
              </button>
            </div>
          `
        : nothing}
    `;
  }
}
